import { useRef, useState } from "react";
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "motion/react";

// soft enough to trail the pointer, stiff enough to settle without wobble
const SPRING_MOUSE = { stiffness: 300, damping: 30, mass: 0.5 };

const canHover = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

/* ============================================================================
   A card that leans toward the pointer, with a soft glare that follows it.

   Tilt is `max` degrees at the edges and springs back flat on leave. The
   glare is a radial wash of the text colour at 15%; it fades in on hover
   only (`.tilt-card:hover`), so a resting row of tiles carries no spot.

   `tilt={false}` keeps the glare and drops the lean: the card stays put and
   only the light moves. The dashboard tiles use it that way - a tile that
   rocks under the pointer made its figures hard to read.

   Decorative, so it is off for reduced motion and for pointers that cannot
   hover (a touch "hover" would leave a card stuck mid-tilt). Everything
   else — class names, children, ARIA — passes straight through.
   ========================================================================== */
const TiltCard = ({ children, max = 12, tilt = true, glare = true, className, ...rest }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [hoverCapable] = useState(canHover);
  const enabled = !reduce && hoverCapable;

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const gx = useMotionValue(50);
  const gy = useMotionValue(50);
  const srx = useSpring(rx, SPRING_MOUSE);
  const sry = useSpring(ry, SPRING_MOUSE);

  const onMove = (e) => {
    const el = ref.current;
    if (!el || !enabled) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    if (tilt) {
      ry.set((px - 0.5) * max);
      rx.set((0.5 - py) * max);
    }
    gx.set(px * 100);
    gy.set(py * 100);
  };

  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  const transform = useMotionTemplate`perspective(1000px) rotateX(${srx}deg) rotateY(${sry}deg)`;
  const glareBg = useMotionTemplate`radial-gradient(circle at ${gx}% ${gy}%, var(--app-text), transparent 50%)`;

  return (
    <motion.div
      {...rest}
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={enabled && tilt ? { transform, transformStyle: "preserve-3d" } : undefined}
      className={
        "tilt-card" + (tilt ? " is-tilting" : "") + (className ? " " + className : "")
      }
    >
      {children}
      {glare && enabled ? (
        <motion.div
          aria-hidden="true"
          className="tilt-card-glare"
          style={{ background: glareBg }}
        />
      ) : null}
    </motion.div>
  );
};

export default TiltCard;
